import { type FC } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { TradeBenefitsHeroStatus } from './TradeBenefitsStatus/TradeBenefitsHeroStatus.tsx';
import { TradeBenefitsHeroActions } from './TradeBenefitsActions/TradeBenefitsHeroActions.tsx';
import { TradeBenefitsHeroStatusContact } from './TradeBenefitsStatus/TradeBenefitsHeroStatusContact.tsx';

import { BASE_IMAGE_URL } from '../tradeBenefitsConstants.ts';
import { useServerVars } from '../../shared/ServerVarsContext.tsx';

import { type TradeBenefitsHero_viewer$key } from './__generated__/TradeBenefitsHero_viewer.graphql.ts';

import sharedStyles from '../TradeBenefitsSharedStyles.scss';
import styles from './TradeBenefitsHero.scss';
import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment TradeBenefitsHero_viewer on Viewer {
        ...TradeBenefitsHeroStatus_viewer
        ...TradeBenefitsHeroActions_viewer
        ...TradeBenefitsHeroStatusContact_viewer
        user(userId: $userId) {
            profile {
                displayName
            }
            tradeFirm {
                name
                publicProfile {
                    profilePhoto
                }
                rewards {
                    currentProgram {
                        programInfo {
                            shortCopy
                        }
                    }
                    statusYears
                }
                ...TradeBenefitsHeroStatus_tradeFirm
                ...TradeBenefitsHeroStatusContact_tradeFirm
            }
        }
    }
`;

const TradeBenefitsHero: FC<{ viewer: TradeBenefitsHero_viewer$key }> = ({ viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const { isMobile } = useServerVars();
    const intl = useIntl();
    const { tradeFirm, profile } = viewer?.user || {};
    const isFirmless = !tradeFirm;
    const { displayName } = profile || {};
    const { name, rewards, publicProfile } = tradeFirm || {};
    const { currentProgram, statusYears } = rewards || {};
    const programCopy = currentProgram?.programInfo?.shortCopy;
    const profilePhoto = publicProfile?.profilePhoto;
    const statusYearsDisplay = statusYears?.length
        ? intl.formatList(statusYears.filter(Boolean).map(String), { style: 'short' })
        : '';

    return (
        <div
            className={classnames(
                sharedStyles.fullWidthContainer,
                styles.container,
                dibsCss.pXlarge
            )}
        >
            <div
                className={classnames(
                    styles.wrapper,
                    dibsCss.grid,
                    dibsCss.gapMedium,
                    dibsCss.maxW1440px,
                    dibsCss.mxAuto
                )}
            >
                <div
                    className={classnames(
                        styles.contentWrapper,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.gapXlarge
                    )}
                >
                    <div className={classnames(styles.content, dibsCss.flex, dibsCss.gapMedium)}>
                        <div
                            className={classnames(
                                styles.imageWrapper,
                                dibsCss.roundedFull,
                                dibsCss.overflowHidden
                            )}
                        >
                            {!isFirmless && (
                                <img
                                    src={profilePhoto || `${BASE_IMAGE_URL}/no_profile_image.jpg`}
                                    alt=""
                                    className={classnames(
                                        dibsCss.hFull,
                                        dibsCss.wFull,
                                        dibsCss.objectCover
                                    )}
                                />
                            )}
                            {isFirmless && (
                                <div
                                    className={classnames(
                                        styles.monogram,
                                        styles.imageWrapper,
                                        dibsCss.roundedFull,
                                        dibsCss.overflowHidden,
                                        dibsCss.bgWhite,
                                        dibsCss.sassyFontHeaderBannerSmallNormal,
                                        dibsCss.flex,
                                        dibsCss.itemsCenter,
                                        dibsCss.justifyCenter
                                    )}
                                >
                                    {displayName?.charAt(0) || ''}
                                </div>
                            )}
                        </div>
                        <div>
                            <h1 className={classnames(styles.title, dibsCss.mt0, dibsCss.mbSmall)}>
                                <FormattedMessage
                                    id="abt.TradeBenefitsHero.title"
                                    defaultMessage="<italic>Welcome back</italic>,{break}{name}"
                                    values={{
                                        name: (isFirmless ? displayName : name) || '',
                                        break: <br />,
                                        italic: content => (
                                            <span className={styles.titleItalic}>{content}</span>
                                        ),
                                    }}
                                />
                            </h1>
                            <div>
                                {!isFirmless && (
                                    <>
                                        <div
                                            className={classnames(
                                                styles.status,
                                                dibsCss.flex,
                                                dibsCss.itemsBaseline,
                                                dibsCss.gapSmall,
                                                dibsCss.textBodyHeaderLargeHeavy
                                            )}
                                        >
                                            <FormattedMessage
                                                id="abt.TradeBenefitsHero.status"
                                                defaultMessage="{programCopy} Member"
                                                values={{ programCopy }}
                                            />
                                            {statusYearsDisplay && (
                                                <div className={dibsCss.textBodyMediumHeavy}>
                                                    {statusYearsDisplay}
                                                </div>
                                            )}
                                        </div>
                                        <p
                                            className={classnames(
                                                styles.description,
                                                dibsCss.sassyFontBodySmall,
                                                dibsCss.mtXsmall
                                            )}
                                        >
                                            <FormattedMessage
                                                id="abt.TradeBenefitsHero.firmless.description"
                                                defaultMessage="Enjoy the benefits of your Trade 1st membership, designed to help you fulfill your creative vision. Earn 1 point for every $1 spent. Every point you earn brings you closer to the next tier and unlocking even more benefits."
                                            />
                                        </p>
                                    </>
                                )}
                                {isFirmless && (
                                    <p
                                        className={classnames(
                                            styles.description,
                                            dibsCss.sassyFontBodyLarge,
                                            dibsCss.mtMedium
                                        )}
                                    >
                                        <FormattedMessage
                                            id="abt.TradeBenefitsHero.description"
                                            defaultMessage="Enjoy the benefits of your Trade 1st membership,{break} designed to help you fulfill your creative vision."
                                            values={{
                                                break: <br />,
                                            }}
                                        />
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <TradeBenefitsHeroStatus viewer={viewer} tradeFirm={tradeFirm} />
                </div>
                <TradeBenefitsHeroActions viewer={viewer} isFirmless={isFirmless} />
                {isMobile && (
                    <TradeBenefitsHeroStatusContact viewer={viewer} tradeFirm={tradeFirm} />
                )}
            </div>
        </div>
    );
};

export { TradeBenefitsHero };
